import { z } from 'zod'
import { AppError } from '@/shared/domain/AppError'
import { legacyInvoiceStatusMap, type InvoiceRecord } from '../../domain/invoice'

const relatedClientSchema = z.object({
  numero: z.string().nullable(),
  prenom: z.string().nullable(),
  nom: z.string().nullable(),
  entreprise: z.string().nullable(),
})

export const legacyInvoiceRowSchema = z.object({
  id: z.uuid(),
  numero: z.string().nullable(),
  client_id: z.uuid(),
  contrat_id: z.uuid().nullable(),
  date: z.string().nullable(),
  sous_total: z.coerce.number(),
  tps: z.coerce.number().nullable(),
  tvq: z.coerce.number().nullable(),
  total: z.coerce.number(),
  solde: z.coerce.number().nullable(),
  statut: z.enum(['brouillon', 'envoyee', 'partiellement_payee', 'payee', 'en_retard', 'annulee']),
  issued_at: z.string().nullable().optional(),
  sent_at: z.string().nullable().optional(),
  cancelled_at: z.string().nullable().optional(),
  cancellation_reason: z.string().nullable().optional(),
  created_at: z.string(),
  updated_at: z.string(),
  deleted_at: z.string().nullable(),
  client: relatedClientSchema.nullable().optional(),
  contrat: z
    .object({
      numero: z.string().nullable(),
      statut: z.string().nullable(),
      status_v2: z.string().nullable(),
    })
    .nullable()
    .optional(),
})

export type LegacyInvoiceRow = z.infer<typeof legacyInvoiceRowSchema>

function optionalText(value: string | null | undefined): string | undefined {
  const normalized = value?.trim()
  return normalized === undefined || normalized === '' ? undefined : normalized
}

/**
 * Nom affiché d'un client : l'entreprise prime, sinon le nom de la personne.
 * Une seule règle, partagée par la fiche et par le suivi des Paiements.
 */
export function displayClientName(
  client: { prenom?: string | null; nom?: string | null; entreprise?: string | null } | null | undefined,
): string {
  return (
    optionalText(client?.entreprise) ??
    optionalText([client?.prenom, client?.nom].filter(Boolean).join(' ')) ??
    'Client inconnu'
  )
}

/** Les montants legacy sont en dollars; le modèle V3 raisonne en cents. */
function toCents(amount: number): number {
  return Math.round(amount * 100)
}

export function mapLegacyInvoice(row: LegacyInvoiceRow): InvoiceRecord {
  const number = optionalText(row.numero)
  if (!number) {
    throw new AppError('UNEXPECTED_ERROR', 'Une facture legacy ne possède pas de numéro.', {
      invoiceId: row.id,
    })
  }

  const client = row.client
  const displayName = displayClientName(client)

  // `envoyee` couvre `ISSUED` et `SENT` : seule la présence de `sent_at`
  // tranche. Les lignes héritées n'en ont pas et restent `SENT`, comme le
  // registre gelé le prescrit.
  const status =
    row.statut === 'envoyee' && row.issued_at && !row.sent_at ? 'ISSUED' : legacyInvoiceStatusMap[row.statut]

  const clientNumber = optionalText(client?.numero)
  const contractNumber = optionalText(row.contrat?.numero)
  const cancellationReason = optionalText(row.cancellation_reason)
  const total = toCents(row.total)
  return {
    id: row.id,
    number,
    status,
    clientId: row.client_id,
    clientDisplayName: displayName,
    ...(clientNumber ? { clientNumber } : {}),
    ...(row.contrat_id ? { contractId: row.contrat_id } : {}),
    ...(contractNumber ? { contractNumber } : {}),
    ...(row.contrat?.statut ? { contractLegacyStatus: row.contrat.statut } : {}),
    ...(row.contrat?.status_v2 ? { contractStatusV2: row.contrat.status_v2 } : {}),
    ...(row.date ? { dueOn: row.date } : {}),
    ...(row.issued_at ? { issuedAt: row.issued_at } : {}),
    ...(row.sent_at ? { sentAt: row.sent_at } : {}),
    ...(row.cancelled_at ? { cancelledAt: row.cancelled_at } : {}),
    ...(cancellationReason ? { cancellationReason } : {}),
    subtotalCents: toCents(row.sous_total),
    taxTotalCents: toCents((row.tps ?? 0) + (row.tvq ?? 0)),
    totalCents: total,
    // Une facture sans solde enregistré est réputée entièrement due : c'est le
    // sens le plus prudent, jamais l'inverse.
    balanceCents: row.solde === null ? total : toCents(row.solde),
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  }
}
